/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useRef, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Calculator } from 'components/Calculator'
import { Globals } from 'components/Globals'
import Chart from 'components/Charts_Chart'
import Key from 'components/Charts_Key'
import { Bars } from 'components/Charts_Bars'
import styled from 'styled-components'
import { StackedBars } from 'components/Charts_StackedBars'
import { BFS_FREQUENCY_AGE } from './bfs_frequenzy_agegroup'
import { COLORS } from 'components/constants'
import { RiskAssessment } from 'components/RiskAssessment'

import { TEXT_DE } from './texts_de'
import { TEXT_FR } from './texts_fr'
import { MovingRow } from 'components/MovingRow'
import { UserText } from 'components/UserText'

// ---
// To deploy a language version:
// - chnage LANGUAGE below to "de" or "fr"
// - copy content of .gitlab-ci_??.yml into .gitlab-ci.yml
// - copy content of .taStandaloner_??c.yml into .taStandalonerc.yml
// - push to production
// - wait till version is fully deployed before deploying othe language
const LANGUAGE: 'de' | 'fr' = 'fr'
// ---
const ARTICLE_ID_DE = '747066300342'
const ARTICLE_ID_FR = '000000000000'
const TEXT = LANGUAGE === 'de' ? TEXT_DE : TEXT_FR
const ARTICLE_ID = LANGUAGE === 'de' ? ARTICLE_ID_DE : ARTICLE_ID_FR
const maxKey = 20

const AGEGROUPS: { [key: string]: string } = TEXT.agegroups

const roundToNearest = (x: number, v: number) => Math.ceil(x / v) * v

const median = (arr: number[]) => {
  const mid = Math.floor(arr.length / 2);
    const nums = [...arr].sort((a, b) => a - b)
  return arr.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2
}

const FREQUENCY_KEY = [
  {
    type: 'shape',
    label: TEXT.evaluation.frequency[0],
    color: [COLORS.purple.dark_1]
  },
  {
    type: 'shape',
    label: TEXT.evaluation.frequency[1],
    color: [COLORS.purple.bright_1]
  },
  {
    type: 'shape',
    label: TEXT.evaluation.frequency[2],
    color: [COLORS.blue.main]
  },
  {
    type: 'shape',
    label: TEXT.evaluation.frequency[3],
    color: [COLORS.blue.bright_2]
  },
  {
    type: 'shape',
    label: TEXT.evaluation.frequency[4],
    color: [COLORS.gray.gray_4]
  }
]

export function AppComponent() {
  const [userInputs, setUserInputs] = useState<UserInputTypes | undefined>()
  const [particpated, setParticipated] = useState(false)
  const [maxXBars, setMaxXBars] = useState(10)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 712)
  const [isLarge, setIsLarge] = useState(window.innerWidth > 2000)
  const [headerelementsonscreen, setHeaderelementsonscreen] = useState(
    isMobile ? 4 : isLarge ? 13 : 8
  )

  const [userGenderData, setUserGenderData] = useState<
    FormatedUserDataGenderType | undefined
  >()
  const [userFrequencyData, setUserFrequencyData] = useState<
    FormatedUserDataFrequencyType | undefined
  >()
  const [userRiskData, setUserRiskData] = useState<
    FormatedUserRiskDataType | undefined
  >()
  const [userDrinkData, setUserDrinkData] = useState<
    FormatedUserDataDrinkType | undefined
  >()
  const [userHeavyData, setUserHeavyData] = useState<
    FormatedUserDataHeavyType | undefined
  >()
  useEffect(() => {
    const handleWindowResize = () => {
      const mob = window.innerWidth < 712
      const lar = window.innerWidth > 2000
      setIsMobile(mob)
      setIsLarge(lar)
      setHeaderelementsonscreen(mob ? 3 : lar ? 13 : 8)
    }
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [])

  const handleUserDataChange = (data: DBDataType | undefined) => {
    if (!data) return
    const { gender, frequency, heavy, risk, drinks } = data

    // GENDER DATA
    if (gender) {
      let f = Object.entries(gender.f).map(([key, value]) => ({
        v: value,
        key: +key.replace('_', '.')
      }))
      const countOverMax_f = f
        .filter(f => f.key >= maxKey)
        .reduce((a, b) => a + b.v, 0)
      f = f.filter(f => f.key < maxKey)
      f.push({ key: maxKey + 1, v: countOverMax_f })
      let m = Object.entries(gender.m).map(([key, value]) => ({
        v: value,
        key: +key.replace('_', '.')
      }))
      const countOverMax_m = m
        .filter(f => f.key > maxKey)
        .reduce((a, b) => a + b.v, 0)
      m = m.filter(f => f.key <= maxKey)
      m.push({ key: maxKey + 1, v: countOverMax_m })
      // Participant Count
      const fSum = f.reduce((a, b) => a + b.v, 0) // Women
      const mSum = m.reduce((a, b) => a + b.v, 0) // Men
      const sum = fSum + mSum // Total

      // Participant Average
      const fAve = f.reduce((a, b) => a + b.key * b.v, 0) / fSum
      const mAve = m.reduce((a, b) => a + b.key * b.v, 0) / mSum
      const ave =
        (m.reduce((a, b) => a + b.key * b.v, 0) +
          f.reduce((a, b) => a + b.key * b.v, 0)) /
        sum

      // Participants Median
      const valueArray = (m: { v: number; key: number }) =>
        [...Array(m.v)].fill(m.key)

      const fMed = median(f.map(valueArray).flat())
      const mMed = median(m.map(valueArray).flat())
      const med = median(
        f
          .concat(m)
          .map(valueArray)
          .flat()
      )
      setUserGenderData({
        f,
        m,
        fSum,
        mSum,
        sum,
        fAve,
        mAve,
        ave,
        fMed,
        mMed,
        med
      })
      const maxx = roundToNearest(
        Math.max(...m.map(m => m.v), ...f.map(m => m.v)) + 5,
        10
      )

      setMaxXBars(maxx)
    }
    if (frequency) {
      const { f, m } = frequency
      const fSum = Object.values(f).reduce((a, b) => a + b, 0)
      const mSum = Object.values(m).reduce((a, b) => a + b, 0)
      const sum = fSum + mSum
      const fRel: { [key: string]: number | string } = {}
      Object.entries(f).map(([key, value]) => {
        fRel[key] = (value * 100) / fSum
      })
      fRel.gender = 'f'
      const mRel: { [key: string]: number | string } = {}
      Object.entries(m).map(([key, value]) => {
        mRel[key] = (value * 100) / mSum
      })
      mRel.gender = 'm'
      setUserFrequencyData({ fSum, mSum, sum, f, m, fRel, mRel })
    }
    if (heavy) {
      const { f, m } = heavy
      const sum = f + m
      setUserHeavyData({ sum, f, m })
    }

    if (risk) {
      const { f, m } = risk
      const fSum = Object.values(f).reduce((a, b) => a + b, 0)
      const mSum = Object.values(m).reduce((a, b) => a + b, 0)
      const sum = fSum + mSum
      const t = {
        none: m.none + f.none,
        low: m.low + f.low,
        middle: m.middle + f.middle,
        high: m.high + f.high
      }

      setUserRiskData({ fSum, mSum, sum, f, m, t })
    }

    if (drinks) {
      const { f, m } = drinks
      const fSum = Object.values(drinks.f).reduce((a, b) => a + b, 0)
      const mSum = Object.values(drinks.m).reduce((a, b) => a + b, 0)
      const sum = fSum + mSum

      setUserDrinkData({ f, m, fSum, mSum, sum })
    }
  }

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <Globals>
        <header className='ui vertical  very fitted  segment'>
          <div className='headerimage'>
            <MovingRow
              imagefile='icons__row_1.png'
              direction="right"
              elementsOnScreen={headerelementsonscreen}
            />
            <MovingRow
              imagefile='icons__row_2.png'
              direction="left"
              elementsOnScreen={headerelementsonscreen}
            />
            <MovingRow
              imagefile='icons__row_3.png'
              direction="right"
              elementsOnScreen={headerelementsonscreen}
            />{' '}
            {isLarge && (
              <MovingRow
                imagefile='icons__row_3.png'
                direction="left"
                elementsOnScreen={headerelementsonscreen}
              />
            )}
            <div className='headerimageoverlay' />
          </div>
        </header>{' '}
        <div className='ui container title'>
          <div className='ui header'>
            <h1>{TEXT.title}</h1>
            <h2>{TEXT.lead}</h2>
          </div>
        </div>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Marc Brupbacher, Sebastian Broschinski
              </div>
              <div className='ta updated'>{TEXT.date}</div>
            </div>
            <div className='ta text'>
              <p>{TEXT.intro}</p>
            </div>
          </div>
        </div>{' '}
        <div className='ui vertical relaxed segment' id='calc'>
          <div className='ui container' id='questions'>
            <div className={isMobile ? '' : 'ui text container'}>
              <Calculator
                getUserInputs={setUserInputs}
                getDBData={handleUserDataChange}
                send={setParticipated}
                TEXT={TEXT.calculator}
              />
            </div>
          </div>
        </div>
        {userInputs && userGenderData && userInputs.gender !== '_' && (
          <>
            <div className='ui vertical  segment'>
              <div className='ui text container'>
                <div className='ta text'>
                  <h3>{TEXT.evaluation.title}</h3>
                  {userRiskData && (
                    <>
                      <UserText
                        userInputs={userInputs}
                        userGenderData={userGenderData}
                        LANGUAGE={LANGUAGE}
                      />
                      <Chart
                        title={TEXT.evaluation.chart1.title}
                        sub={
                          TEXT.evaluation.chart1.sub[0] +
                          userGenderData.sum +
                          TEXT.evaluation.chart1.sub[1]
                        }
                      >
                        <ChartWrapper className='aa'>
                          <GenderTitle className='m'>
                            {TEXT.evaluation.chart1.gender[0]}{' '}
                            <GenderSub className='m'>
                              ({userGenderData.mSum})
                            </GenderSub>
                          </GenderTitle>

                          <GenderTitle className='f'>
                            {TEXT.evaluation.chart1.gender[1]}{' '}
                            <GenderSub className='f'>
                              ({userGenderData.fSum})
                            </GenderSub>
                          </GenderTitle>
                          <GenderVerticalKey>
                            <div>{TEXT.evaluation.chart1.key[0]}</div>
                            <br />
                            <img src={require('images/icons_arrow_up.png')} />
                          </GenderVerticalKey>
                          <Bars
                            maxKey={maxKey}
                            marked={{
                              data: userInputs,
                              label: TEXT.evaluation.userMarker
                            }}
                            className='Bars'
                            data={[
                              userGenderData.m
                                .filter(f => f.v !== 0)
                                .sort((a, b) => a.key - b.key),
                              userGenderData.f
                                .filter(f => f.v !== 0)
                                .sort((a, b) => a.key - b.key)
                            ]}
                            xDomain={[0, maxXBars]}
                            height={350}
                          />
                          <KeyWrapper>
                            <div className='left'>
                              <img
                                src={require('images/icons_arrow_left.png')}
                              />
                              {TEXT.evaluation.chart1.key[1]}{' '}
                            </div>

                            <div className='middle' />

                            <div className='right'>
                              {TEXT.evaluation.chart1.key[2]}{' '}
                              <img
                                src={require('images/icons_arrow_right.png')}
                              />
                            </div>
                          </KeyWrapper>
                        </ChartWrapper>
                      </Chart>
                    </>
                  )}
                  {userGenderData && (
                    <Chart
                      title={TEXT.evaluation.chart2.title}
                      sub={
                        TEXT.evaluation.chart2.sub[0] +
                        userGenderData.sum +
                        TEXT.evaluation.chart2.sub[1]
                      }
                    >
                      <ChartWrapper>
                        {userInputs.gender !== '_' && (
                          <>
                            {userInputs && userFrequencyData && (
                              <>
                                <StackedBars
                                  gender="m"
                                  data={[
                                    {
                                      ...userFrequencyData.mRel,
                                      agegroup: userInputs.age
                                    }
                                  ]}
                                  rowLabel={(e: { [key: string]: any }) =>
                                    e.gender === 'f'
                                      ? TEXT.evaluation.chart1.gender[1] +
                                        ' (' +
                                        userGenderData.fSum +
                                        ')'
                                      : TEXT.evaluation.chart1.gender[0] +
                                        ' (' +
                                        userGenderData.mSum +
                                        ')'
                                  }
                                  className='readerstacked'
                                  height={42}
                                  marked={{
                                    data: userInputs,
                                    label: TEXT.evaluation.userMarker,
                                    show: userInputs.gender === 'm'
                                  }}
                                  groupLabels={TEXT.evaluation.frequency}
                                />
                                <StackedBars
                                  gender="f"
                                  data={[
                                    {
                                      ...userFrequencyData.fRel,
                                      agegroup: userInputs.age
                                    }
                                  ]}
                                  rowLabel={(e: { [key: string]: any }) =>
                                    e.gender === 'f'
                                      ? TEXT.evaluation.chart1.gender[1] +
                                        ' (' +
                                        userGenderData.fSum +
                                        ')'
                                      : TEXT.evaluation.chart1.gender[0] +
                                        ' (' +
                                        userGenderData.mSum +
                                        ')'
                                  }
                                  className='readerstacked'
                                  height={42}
                                  marked={{
                                    data: userInputs,
                                    label: TEXT.evaluation.userMarker,
                                    show: userInputs.gender === 'f'
                                  }}
                                  groupLabels={TEXT.evaluation.frequency}
                                />
                              </>
                            )}
                            <Key entries={FREQUENCY_KEY} />
                          </>
                        )}
                      </ChartWrapper>
                    </Chart>
                  )}
                  {userGenderData && (
                    <Chart
                      title={TEXT.evaluation.chart3.title}
                      sub={TEXT.evaluation.chart3.sub}
                    >
                      <ChartWrapper>
                        {userInputs && userInputs.gender !== '_' && (
                          <>
                            <GenderTitle className='left '>
                              {TEXT.evaluation.chart3.gender[0]}
                            </GenderTitle>
                            <StackedBars
                              gender="m"
                              data={BFS_FREQUENCY_AGE.filter(
                                f => f.gender === 'm'
                              )}
                              className='malestacked'
                              rowLabel={(e: { [key: string]: any }) =>
                                AGEGROUPS[e.agegroup]
                              }
                              marked={{
                                data: userInputs,
                                label: TEXT.evaluation.userMarker,
                                show: userInputs.gender === 'm'
                              }}
                              height={280}
                              groupLabels={TEXT.evaluation.frequency}
                            />{' '}
                            <Key entries={FREQUENCY_KEY} />
                          </>
                        )}
                        {userInputs && userInputs.gender !== '_' && (
                          <>
                            <GenderTitle className='left'>
                              {TEXT.evaluation.chart3.gender[1]}
                            </GenderTitle>
                            <StackedBars
                              gender="f"
                              data={BFS_FREQUENCY_AGE.filter(
                                f => f.gender === 'f'
                              )}
                              rowLabel={(e: { [key: string]: any }) =>
                                AGEGROUPS[e.agegroup]
                              }
                              marked={{
                                data: userInputs,
                                label: TEXT.evaluation.userMarker,
                                show: userInputs.gender === 'f'
                              }}
                              className='malestacked'
                              height={280}
                              groupLabels={TEXT.evaluation.frequency}
                            />
                          </>
                        )}
                        <Key entries={FREQUENCY_KEY} />
                        <Source>
                          {TEXT.evaluation.source[0]}{' '}
                          <a
                            href='https://www.bfs.admin.ch/bfs/de/home/statistiken/gesundheit/determinanten/alkohol.html'
                            target="_blank" rel="noreferrer"
                          >
                            {TEXT.evaluation.source[1]}
                          </a>
                        </Source>
                      </ChartWrapper>
                    </Chart>
                  )}
                </div>
              </div>
            </div>
            <div className='ui vertical very fitted segment'>
              <div className='ui text container'>
                {userInputs && userInputs.gender !== '_' && userRiskData && (
                  <>
                    <RiskAssessment
                      user={userInputs}
                      data={userRiskData}
                      labels={TEXT.risk}
                      lang={LANGUAGE}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}
        <Footer
          articleId={ARTICLE_ID}
          credits={TEXT.footer.credits as [string, string][]}
          questions={TEXT.footer.questions}
        />
      </Globals>
    </div>
  )
}

const Source = styled.div`
  font-size: 12px;
`
const KeyWrapper = styled.div`
  display: flex;
  width: 100%;
  font-size: 12px;
  padding-top: 0px;
  line-height: 1.5em;
  img {
    max-width: 15px;
  }

  div {
    display: flex;
    align-items: baseline;
    flex: 0 0 37%;
    &.left {
      padding-right: 15%;
      img {
        margin-right: 5px;
      }
    }
    &.middle {
      flex: 0 0 26%;
      text-align: center;
      flex-direction: column;
      align-items: center;
      margin-top: 0;
      padding-left: 10px;
      padding-right: 10px;
    }
    &.right {
      padding-left: 15%;

      img {
        margin-left: 5px;
      }
      text-align: right;
    }
  }
  @media only screen and (max-width: 711px) {
    div {
      flex: 0 0 37%;
      &.left {
        padding-right: 0;
      }
      &.middle {
        padding: 0;
      }
      &.right {
        padding: 0;
      }
    }
  }
`
const GenderVerticalKey = styled.div`
  width: 100%;
  display: inline-block;
  text-align: center;
  font-size: 12px;
  line-height: 1.5em;
  div {
    display: inline-block;

    width: 30%;
    @media only screen and (max-width: 711px) {
      width: 100%;
    }
  }
  img {
    margin-top: 10px;
    clear: both;
    width: 16px;
  }
`
const GenderTitle = styled.div`
  display: inline-block;
  font-size: 26px;
  width: 50%;
  text-align: center;
  font-weight: 400;
  letter-spacing: 1px;
  line-height: 1em;

  margin-top: 15px;
  margin-bottom: 15px;
  &.left {
    text-align: left;
    width: 100%;
  }

  /* &.f {
    color: var(--orange-main);
  }
  &.m {
    color: var(--petrol-main);
  } */
`
const GenderSub = styled.div`
  display: inline-block;
  font-size: 15px;
  text-align: center;
  font-weight: 400;
  line-height: 1em;
  letter-spacing: 0.5px;
  color: var(--brandblue-bright-3);
  width: 100%;
  &.left {
    width: 100%;
  }
  /* &.f {
    color: var(--orange-main);
  }
  &.m {
    color: var(--petrol-main);
  } */
  b {
    font-weight: 700;
    font-size: 16px;
  }
  @media only screen and (max-width: 711px) {
    font-size: 12px;
    b {
      font-size: 15px;
    }
  }
`
const ChartWrapper = styled.div`
  flex: 0 0 100%;
  @media only screen and (max-width: 711px) {
    flex: 0 0 calc(100%);
    max-width: calc(100%);
  }
`
